// web-commands.ts — the PER-APP half of the web shape: maps oriora_flashcards' Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. Unlike apps with Rust-computed derived signals, the ENTIRE
// scheduling brain of this app (FSRS-4.5) already lives client-side in src/fsrs.ts, so the web
// surface is feature-complete: apply_review just persists the state TS computed, same as native.
// The photo/OCR commands are native-only (Apple Vision + on-device file store); on web they
// degrade to empty results so the UI's own fallbacks ("paste the notes instead") take over.

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const DECKS = "decks";
const CARDS = "cards";
const REVIEW_LOG = "review_log";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

/** Normalize a tag — lowercase, trim, collapse internal whitespace (mirrors db.rs::normalize). */
function normalizeTag(raw: unknown): string {
  return String(raw ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

/** Validate a card source, defaulting to "typed" (mirrors db.rs). */
function normalizeSource(s: unknown): string {
  const v = String(s ?? "typed");
  return v === "typed" || v === "notes" || v === "photo" ? v : "typed";
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  const cardsOf = (deckId: string) =>
    db.all(CARDS).filter((c) => (c as { deck_id?: string }).deck_id === deckId);

  const hydrateDeck = (d: Row) => ({ ...d, card_count: cardsOf(d.id).length });

  const newCardRow = (deckId: string, i: Record<string, unknown>): Row => ({
    id: uuid(),
    deck_id: deckId,
    front: String(i.front ?? "").trim(),
    back: String(i.back ?? "").trim(),
    tag: normalizeTag(i.tag),
    source: normalizeSource(i.source),
    kind: (i.kind === "cloze" ? "cloze" : "basic"),
    // FSRS scheduling state — new cards are due immediately (mirrors db.rs defaults).
    state: 0,
    due: nowIso(),
    stability: 0,
    difficulty: 0,
    reps: 0,
    lapses: 0,
    last_review: null as string | null,
    created_at: nowIso(),
    updated_at: nowIso(),
  });

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Decks ──────────────────────────────────────────────────────────────────
    list_decks: () =>
      db
        .all(DECKS)
        .sort((a, b) =>
          String((a as { name?: string }).name ?? "").localeCompare(
            String((b as { name?: string }).name ?? ""),
            undefined,
            { sensitivity: "base" },
          ),
        )
        .map(hydrateDeck),
    create_deck: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const row = {
        id: uuid(),
        name: String(i.name ?? "").trim(),
        description: String(i.description ?? "").trim(),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return hydrateDeck(db.insert(DECKS, row));
    },
    update_deck: ({ id, patch }) => {
      const p = { ...((patch ?? {}) as Record<string, unknown>) };
      if ("name" in p) p.name = String(p.name ?? "").trim();
      if ("description" in p) p.description = String(p.description ?? "").trim();
      db.update(DECKS, String(id), { ...p, updated_at: nowIso() });
      return null;
    },
    delete_deck: ({ id }) => {
      const deckId = String(id);
      // Cascade: cards + review log go with the deck (mirrors ON DELETE CASCADE).
      for (const c of cardsOf(deckId)) db.remove(CARDS, c.id);
      for (const l of db.all(REVIEW_LOG)) {
        if ((l as { deck_id?: string }).deck_id === deckId) db.remove(REVIEW_LOG, l.id);
      }
      db.remove(DECKS, deckId);
      return null;
    },

    // ── Cards ──────────────────────────────────────────────────────────────────
    list_cards: ({ deckId, limit }) => {
      let rows = db.all(CARDS);
      if (deckId) rows = rows.filter((c) => (c as { deck_id?: string }).deck_id === String(deckId));
      rows.sort((a, b) =>
        String((a as { created_at?: string }).created_at ?? "").localeCompare(
          String((b as { created_at?: string }).created_at ?? ""),
        ),
      );
      const n = Number(limit);
      return Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
    },
    create_card: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      return db.insert(CARDS, newCardRow(String(i.deck_id ?? ""), i));
    },
    create_cards: ({ deckId, cards }) => {
      const list = Array.isArray(cards) ? cards : [];
      let n = 0;
      for (const c of list) {
        const i = (c ?? {}) as Record<string, unknown>;
        if (!String(i.front ?? "").trim() || !String(i.back ?? "").trim()) continue;
        db.insert(CARDS, newCardRow(String(deckId), i));
        n++;
      }
      return n;
    },
    update_card: ({ id, patch }) => {
      const p = { ...((patch ?? {}) as Record<string, unknown>) };
      if ("front" in p) p.front = String(p.front ?? "").trim();
      if ("back" in p) p.back = String(p.back ?? "").trim();
      if ("tag" in p) p.tag = normalizeTag(p.tag);
      if ("deck_id" in p) p.deck_id = String(p.deck_id ?? "");
      db.update(CARDS, String(id), { ...p, updated_at: nowIso() });
      return null;
    },
    delete_card: ({ id }) => {
      const cardId = String(id);
      for (const l of db.all(REVIEW_LOG)) {
        if ((l as { card_id?: string }).card_id === cardId) db.remove(REVIEW_LOG, l.id);
      }
      db.remove(CARDS, cardId);
      return null;
    },

    // ── Reviews — persist the FSRS state computed in TS + one log row ───────────
    apply_review: ({ id, review }) => {
      const r = (review ?? {}) as Record<string, unknown>;
      const card = db.get(CARDS, String(id));
      if (!card) throw new Error("not_found");
      db.update(CARDS, String(id), {
        state: Number(r.state ?? 0),
        due: String(r.due ?? nowIso()),
        stability: Number(r.stability ?? 0),
        difficulty: Number(r.difficulty ?? 0),
        reps: Number(r.reps ?? 0),
        lapses: Number(r.lapses ?? 0),
        last_review: r.last_review == null ? null : String(r.last_review),
        updated_at: nowIso(),
      });
      db.insert(REVIEW_LOG, {
        id: uuid(),
        card_id: String(id),
        deck_id: String((card as { deck_id?: string }).deck_id ?? ""),
        grade: Number(r.grade ?? 0),
        state_before: Number(r.state_before ?? 0),
        elapsed_days: Number(r.elapsed_days ?? 0),
        scheduled_days: Number(r.scheduled_days ?? 0),
        reviewed_at: String(r.reviewed_at ?? nowIso()),
      });
      return null;
    },
    list_review_log: ({ deckId, limit }) => {
      let rows = db.all(REVIEW_LOG);
      if (deckId) rows = rows.filter((l) => (l as { deck_id?: string }).deck_id === String(deckId));
      rows.sort((a, b) =>
        String((b as { reviewed_at?: string }).reviewed_at ?? "").localeCompare(
          String((a as { reviewed_at?: string }).reviewed_at ?? ""),
        ),
      );
      const n = Number(limit);
      return Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
    },

    // ── Native-only (Apple Vision OCR + on-device photo store). Web has no on-device file
    //    store or OCR engine, so these return empty and the generate sheet's own fallback
    //    ("paste the notes instead") takes over.
    save_photo: () => "",
    ocr_image: () => "",
  };
}
